import React, { useEffect } from 'react';
import { TaxFormProvider, useTaxForm } from './TaxFormContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileTaxFormContent } from './MobileTaxFormContent';
export { TaxFormProvider, useTaxForm };
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { StepProgress } from './StepProgress';
import { NavigationButtons } from './NavigationButtons';
import { Step1AccountInfo } from './steps/Step1AccountInfo';
import { Step2PersonalInfo } from './steps/Step2PersonalInfo';
import { Step3IncomeDetails } from './steps/Step3IncomeDetails';
import { Step4BankDetails } from './steps/Step4BankDetails';
import { Step5AssetsLiabilities } from './steps/Step5AssetsLiabilities';
import { Step6Review } from './steps/Step6Review';
import { Save } from 'lucide-react';
import Loader from '@/components/Loader';

// This component no longer needs to manage an `onSubmit` prop.
export const TaxFormContent: React.FC<{ isModal?: boolean }> = ({ isModal = false }) => {
  const { currentStep, isLoading, isSaving } = useTaxForm();

  useEffect(() => {
    if (!isModal) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [currentStep, isModal]);

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1AccountInfo />;
      case 2: return <Step2PersonalInfo />;
      case 3: return <Step3IncomeDetails />;
      case 4: return <Step4BankDetails />;
      case 5: return <Step5AssetsLiabilities />;
      case 6: return <Step6Review />; // This component now handles its own submission logic.
      default: return <Step1AccountInfo />;
    }
  };

  if (isModal) {
    return (
      <div className="flex flex-col h-full bg-slate-50 w-full">
        <div className="p-4 border-b">
          <StepProgress />
        </div>
        <div className="flex-grow overflow-y-auto p-4 w-full">
          <div className="max-w-4xl mx-auto w-full">
             <div className="animate-fade-in w-full">
               {isLoading ? <Loader /> : renderStep()}
             </div>
             <div className="mt-4">
               <NavigationButtons />
             </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Header />
      <main className="flex-grow container mx-auto px-4 pt-24 pb-8">
        <div className="text-center mb-8 animate-slide-up">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent mb-4">
            Tax Return Form
          </h1>
          <h2 className="text-2xl md:text-3xl font-urdu text-muted-foreground mb-4">
            ٹیکس ریٹرن فارم
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Complete your tax return information step by step. Your progress is automatically saved.
          </p>
          <p className="text-muted-foreground font-urdu mt-2">
            اپنی ٹیکس ریٹرن کی معلومات قدم بہ قدم مکمل کریں۔ آپ کی پیش قدمی خودکار طور پر محفوظ ہو جاتی ہے۔
          </p>

          {(isLoading || isSaving) && (
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-primary">
              <Save className="h-4 w-4 animate-pulse" />
              {isLoading ? "Loading Data..." : "Saving..."}
            </div>
          )}
        </div>

        <StepProgress />

        <div className="max-w-4xl mx-auto">
          <div className="animate-fade-in">
            {isLoading ? <Loader /> : renderStep()}
          </div>

          {/* NavigationButtons no longer needs the onSubmit prop */}
          <NavigationButtons />
        </div>
      </main>
      <Footer />
    </div>
  );
};

import { Calendar, ArrowRight, ShieldCheck, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { computeDefaultTaxYear } from '@/utils/taxEngine';

import { TaxYearGateway } from '@/components/dashboard/FileReturnTab';

// The main wizard component now acts as a gateway
export const TaxFormWizard: React.FC = () => {
  const isMobile = useIsMobile();
  const [confirmedYear, setConfirmedYear] = React.useState<string | null>(null);
  const [initialData, setInitialData] = React.useState<any>(null);
  const [isReadOnly, setIsReadOnly] = React.useState(false);
  const [isNew, setIsNew] = React.useState(false);

  if (!confirmedYear && !initialData) {
    return (
      <div className="flex flex-col min-h-screen bg-slate-50 relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-br from-indigo-600 via-blue-500 to-sky-400 opacity-10 rounded-b-[100px] blur-3xl" />
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-blue-400/20 blur-[120px]" />
        
        <Header />
        
        <main className="flex-grow flex items-center justify-center p-4 pt-32 pb-12 relative z-10 w-full">
           <TaxYearGateway 
              onProceedBlank={(year: string) => { setConfirmedYear(year); setIsNew(true); }}
              onRecoverDraft={(data: any) => { setInitialData(data); setConfirmedYear(data.tax_year); setIsNew(false); }}
              onReviseReturn={(data: any) => { 
                  const copy = { ...data };
                  delete copy.id;
                  copy.tax_year = copy.tax_year.includes('(Revised)') ? copy.tax_year : `${copy.tax_year} (Revised)`;
                  copy.filing_stage = 'draft';
                  copy.status = 'Revised Draft';
                  setInitialData(copy);
                  setConfirmedYear(copy.tax_year);
                  setIsNew(true);
              }}
              onCancel={() => window.history.back()}
           />
        </main>
        <Footer />
      </div>
    );
  }

  // Key the provider by the ID or tax_year so React freshly mounts it when switching
  const formKey = initialData?.id || initialData?.tax_year || confirmedYear || 'new-form';

  return (
    <TaxFormProvider key={formKey} targetTaxYear={confirmedYear || undefined} initialData={initialData} isNew={isNew} readOnly={isReadOnly}>
      {isMobile ? <MobileTaxFormContent /> : <TaxFormContent />}
    </TaxFormProvider>
  );
};
